import { AlertTriangle, CheckCircle as CheckIcon } from 'lucide-react'
import { formatCurrency, InfoTooltip } from '../JobAutoCalculatorHelpers'
import { computeEligibility, computeExistingMonthly } from '../jobautoCalc'
import { cn } from '../../../shared'

export interface ResultCar {
  label: string
  eigenanteil: number
  gwv: number
}

interface Props {
  bruttoMonatlich: number
  monateImJahr: number
  /** Net (ex-VAT) monthly salary conversion of the new JobAuto. */
  neuesJobautoRate: number
  bavAN: number
  /** True when bavAN is the assumed 430 € fallback (no bAV on the payslip). */
  bavAssumed: boolean
  firmenfitness: number
  fahrradGehaltsumwandlung: number
  cars: ResultCar[]
  excludedCarIndices: ReadonlySet<number>
  carGehaltsumwandlungFlat: number
  carGwvFlat: number
  mindestgrenze: number
  onMindestgrenzeChange: (value: number) => void
}

export default function EligibilityResult({
  bruttoMonatlich,
  monateImJahr,
  neuesJobautoRate,
  bavAN,
  bavAssumed,
  firmenfitness,
  fahrradGehaltsumwandlung,
  cars,
  excludedCarIndices,
  carGehaltsumwandlungFlat,
  carGwvFlat,
  mindestgrenze,
  onMindestgrenzeChange,
}: Props) {
  if (bruttoMonatlich <= 0) return null

  const existingMonthly = computeExistingMonthly({
    bavAN,
    firmenfitness,
    fahrradGehaltsumwandlung,
    cars: cars.map(c => ({ eigenanteil: c.eigenanteil, gwv: c.gwv })),
    excludedCarIndices,
    carGehaltsumwandlungFlat,
    carGwvFlat,
  })
  const { jahresBruttoMitJobAuto, belowMindestgehalt, ok } = computeEligibility({
    bruttoMonatlich,
    monateImJahr,
    neuesJobautoRate,
    existingMonthly,
    mindestgrenze,
  })

  const hasParsedCars = cars.length > 0
  // Guard inputs that would silently produce a false "eligible":
  //  - a non-positive threshold makes `belowMindestgehalt` never true;
  //  - a non-positive JobAuto rate means the portal rate is missing/0, so the
  //    floor would be checked without any JobAuto deduction at all.
  // In both cases we suspend the check instead of implying eligibility.
  const invalidThreshold = mindestgrenze <= 0
  const missingJobautoRate = neuesJobautoRate <= 0
  const inputBlocked = invalidThreshold || missingJobautoRate
  const blockedMessage = missingJobautoRate
    ? 'JobAuto-Nettorate fehlt oder ist 0 — Prüfung ausgesetzt.'
    : 'Bitte eine gültige interne Mindestgrenze (> 0) eingeben — Prüfung ausgesetzt.'

  const cardTone = inputBlocked
    ? 'bg-amber-50 border border-amber-200'
    : belowMindestgehalt
      ? 'bg-red-50 border border-red-200'
      : 'bg-green-50 border border-green-200'

  return (
    <div className={cn('rounded-xl p-5 space-y-4', cardTone)}>
      <div className="flex items-start gap-3">
        {inputBlocked ? (
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-500" />
        ) : belowMindestgehalt ? (
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-red-500" />
        ) : (
          <CheckIcon className="h-5 w-5 shrink-0 mt-0.5 text-green-500" />
        )}
        <div>
          {inputBlocked ? (
            <div className="font-semibold text-amber-700">
              {blockedMessage}
            </div>
          ) : (
            <>
              <div className={cn('font-semibold', belowMindestgehalt ? 'text-red-700' : 'text-green-700')}>
                {belowMindestgehalt
                  ? 'Interne Mindestgrenze unterschritten — nicht förderfähig'
                  : 'Interne Mindestgrenze eingehalten — förderfähig ✓'}
              </div>
              <div
                className={cn(
                  'text-3xl font-bold mt-1',
                  belowMindestgehalt ? 'text-red-700' : 'text-green-700',
                )}
              >
                {formatCurrency(jahresBruttoMitJobAuto)}
                <span className="text-base font-normal text-zinc-500 ml-2">Jahresbrutto nach Abzug</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="border-t border-zinc-200/60 pt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-600">
        <span className="text-zinc-400">Jahresbrutto (12 × {formatCurrency(bruttoMonatlich)})</span>
        <span className="font-medium text-right">{formatCurrency(bruttoMonatlich * 12)}</span>

        {bavAN > 0 && (
          <>
            <span className="text-zinc-400">
              − bAV Eigenanteil (×12)
              {bavAssumed && (
                <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-medium align-middle">
                  Annahme
                </span>
              )}
            </span>
            <span className="font-medium text-right text-red-600">− {formatCurrency(bavAN * 12)}</span>
          </>
        )}
        {firmenfitness > 0 && (
          <>
            <span className="text-zinc-400">− Firmenfitness (×12)</span>
            <span className="font-medium text-right text-red-600">
              − {formatCurrency(firmenfitness * 12)}
            </span>
          </>
        )}

        {hasParsedCars
          ? cars.map((car, i) => {
              if (excludedCarIndices.has(i)) return null
              return (
                <div key={`car-${i}`} className="contents">
                  {car.eigenanteil > 0 && (
                    <>
                      <span className="text-zinc-400">− {car.label} Eigenanteil (×12)</span>
                      <span className="font-medium text-right text-red-600">
                        − {formatCurrency(car.eigenanteil * 12)}
                      </span>
                    </>
                  )}
                  {car.gwv > 0 && (
                    <>
                      <span className="text-zinc-400">− {car.label} GwV (×12)</span>
                      <span className="font-medium text-right text-red-600">
                        − {formatCurrency(car.gwv * 12)}
                      </span>
                    </>
                  )}
                </div>
              )
            })
          : (
            <>
              {carGehaltsumwandlungFlat > 0 && (
                <>
                  <span className="text-zinc-400">− PKW Eigenanteil (×12)</span>
                  <span className="font-medium text-right text-red-600">
                    − {formatCurrency(carGehaltsumwandlungFlat * 12)}
                  </span>
                </>
              )}
              {carGwvFlat > 0 && (
                <>
                  <span className="text-zinc-400">− PKW GwV (×12)</span>
                  <span className="font-medium text-right text-red-600">
                    − {formatCurrency(carGwvFlat * 12)}
                  </span>
                </>
              )}
            </>
          )}

        {fahrradGehaltsumwandlung > 0 && (
          <>
            <span className="text-zinc-400">− Jobrad Rate (×12)</span>
            <span className="font-medium text-right text-red-600">
              − {formatCurrency(fahrradGehaltsumwandlung * 12)}
            </span>
          </>
        )}

        <span className="text-zinc-400 flex items-center gap-1">
          − Neues JobAuto ({monateImJahr} × {formatCurrency(neuesJobautoRate)})
          <InfoTooltip>
            Netto-Gehaltsumwandlung (ohne MwSt.) des neuen JobAutos — FINN ist
            vorsteuerabzugsberechtigt, daher wird die Nettorate abgezogen. Der geldwerte Vorteil
            (GwV) des neuen Wagens fließt NICHT in diese Mindestgrenze ein — geprüft wird das
            verbleibende Jahresbrutto nach der Entgeltumwandlung.
          </InfoTooltip>
        </span>
        <span className="font-medium text-right text-red-600">
          − {formatCurrency(neuesJobautoRate * monateImJahr)}
        </span>

        <span
          className={cn(
            'font-semibold border-t border-zinc-200 pt-1.5',
            ok ? 'text-green-700' : 'text-red-600',
          )}
        >
          = Jahresbrutto nach Abzug
        </span>
        <span
          className={cn(
            'font-bold text-right border-t border-zinc-200 pt-1.5',
            ok ? 'text-green-700' : 'text-red-600',
          )}
        >
          {formatCurrency(jahresBruttoMitJobAuto)}
        </span>

        <span className="text-zinc-400 flex items-center gap-1">
          Interne Mindestgrenze
          <InfoTooltip>
            Internes Mindest-Jahresbrutto nach allen Entgeltumwandlungen. Anpassbar, falls sich die
            interne Vorgabe ändert.
          </InfoTooltip>
        </span>
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            step={1000}
            min={0}
            value={mindestgrenze || ''}
            onChange={e => onMindestgrenzeChange(Number(e.target.value))}
            className="w-28 text-right px-2 py-1 border border-zinc-200 bg-white text-zinc-900 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
          <span className="text-zinc-400">€</span>
        </div>
      </div>
    </div>
  )
}
